<script setup lang="ts">
import type { PageSectionProps } from '#ui/types';
import lodashOmit from 'lodash-es/omit';
import lodashPick from 'lodash-es/pick';

export interface StoryblokUiPageSectionProps {
    blok: StoryblokUiPageSection;
}

// PROPS
const { blok } = defineProps<StoryblokUiPageSectionProps>();

// COMPOSABLES
const { classes } = useStoryblokComponentClass(() => lodashPick(blok, ['maxWidth', 'anchorId']));

// DATA
const pageSectionProps = computed<PageSectionProps>(() =>
    lodashOmit(storyblokOmitEmptyValues<StoryblokUiPageSection>(blok), ['_uid', 'component', 'anchorId', 'description', 'features', 'links'])
);
const hasThreeColLayout = computed(() => blok.orientation === 'horizontal' && blok.columns === '3');
const ui = computed(() =>
    hasThreeColLayout.value
        ? {
              container: 'lg:grid-cols-3',
              wrapper: 'lg:col-span-2',
          }
        : {}
);
</script>

<template>
    <UPageSection v-bind="pageSectionProps" :id="blok.anchorId" v-editable="blok" as="section" :class="classes" :ui="ui">
        <template v-if="!!blok.description" #description>
            <StoryblokRichTextOverride :doc="blok.description" class="max-w-none" />
        </template>
        <template v-if="blok.features?.length" #features>
            <StoryblokComponent v-for="f in blok.features" :key="f._uid" :blok="f" />
        </template>
        <template v-if="blok.links?.length" #links>
            <StoryblokComponent v-for="l in blok.links" :key="l._uid" :blok="l" />
        </template>
        <template v-if="blok.body?.length" #default>
            <StoryblokComponent v-for="b in blok.body" :key="b._uid" :blok="b" />
        </template>
    </UPageSection>
</template>
